// src/core/PluginManager.ts
import { IPlugin, IPipelinePlugin, IStandalonePlugin } from '../types/plugin';
import { PluginContext } from '../types/plugin_context';
import { IUniqodeInsightsClient } from '../types/sdk';
import { SdkConfig } from '../types/config';
import { QueuedEvent } from '../types';
import { PluginCategory, PluginType } from '../types/plugin_categories';
import { getPluginConfig, validateInheritanceConfig } from '../utils/configInheritance';
import { ILogger } from '../utils/logger';

// Categorized plugin storage for pipeline plugins
interface CategorizedPlugins {
  enrichment: IPipelinePlugin[];
  preTransport: IPipelinePlugin[];
  postTransport: IPipelinePlugin[];
}

// Transport result for post-transport plugins
export interface TransportResult {
  success: boolean;
  error?: Error;
  event: QueuedEvent;
}

export class PluginManager {
  private categorizedPlugins: CategorizedPlugins = {
    enrichment: [],
    preTransport: [],
    postTransport: [],
  };
  private standalonePlugins: IStandalonePlugin[] = [];
  private readonly client: IUniqodeInsightsClient;
  private sdkConfig?: SdkConfig;
  private logger?: ILogger;

  constructor(client: IUniqodeInsightsClient) {
    this.client = client;
  }

  public setSdkConfig(config: SdkConfig): void {
    this.sdkConfig = config;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.logger = config.logger!; // Logger should always be provided by UniqodeInsightsClient
    this.logger.debug('PluginManager SDK config set');
  }

  /**
   * Adds a plugin to the manager. Supports both new categorized plugins and legacy plugins.
   * Does not initialize the plugin.
   * @param plugin The plugin instance to add.
   * @param order Optional order number (only used for legacy plugins).
   * @returns The added plugin instance if successful, undefined if a plugin with the same name already exists.
   */
  public add(plugin: IPlugin): IPlugin | undefined {
    // Check for duplicate names across all plugin types
    const existingNames = [
      ...this.categorizedPlugins.enrichment.map(p => p.name),
      ...this.categorizedPlugins.preTransport.map(p => p.name),
      ...this.categorizedPlugins.postTransport.map(p => p.name),
      ...this.standalonePlugins.map(p => p.name),
    ];

    if (existingNames.includes(plugin.name)) {
      this.logger?.warn(`Plugin "${plugin.name}" already exists. Skipping`);
      return undefined;
    }

    if (plugin.pluginType === PluginType.PIPELINE) {
      const pipelinePlugin = plugin;
      switch (pipelinePlugin.category) {
        case PluginCategory.ENRICHMENT:
          this.categorizedPlugins.enrichment.push(pipelinePlugin);
          this.logger?.debug(`Pipeline plugin "${plugin.name}" added to ENRICHMENT`);
          break;
        case PluginCategory.PRE_TRANSPORT:
          this.categorizedPlugins.preTransport.push(pipelinePlugin);
          this.logger?.debug(`Pipeline plugin "${plugin.name}" added to PRE_TRANSPORT`);
          break;
        case PluginCategory.POST_TRANSPORT:
          this.categorizedPlugins.postTransport.push(pipelinePlugin);
          this.logger?.debug(`Pipeline plugin "${plugin.name}" added to POST_TRANSPORT`);
          break;
        default:
          this.logger?.error(`Unknown pipeline category for plugin "${plugin.name}"`);
          return undefined;
      }
    } else if (plugin.pluginType === PluginType.STANDALONE) {
      const standalonePlugin = plugin;
      this.standalonePlugins.push(standalonePlugin);
      this.logger?.debug(`Standalone plugin "${plugin.name}" added`);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.logger?.error(`Unknown plugin type for plugin "${(plugin as any).name}"`);
      return undefined;
    }

    return plugin;
  }

  /**
   * Removes a plugin by its name and calls its destroy method if it exists.
   * @param pluginName The name of the plugin to remove.
   * @returns True if the plugin was found and removed, false otherwise.
   */
  public remove(pluginName: string): boolean {
    // Check enrichment plugins
    let pluginIndex = this.categorizedPlugins.enrichment.findIndex(p => p.name === pluginName);
    if (pluginIndex !== -1) {
      const [removedPlugin] = this.categorizedPlugins.enrichment.splice(pluginIndex, 1);
      if (removedPlugin.destroy) {
        this.logger?.debug(`Destroying enrichment plugin "${pluginName}".`);
        removedPlugin.destroy();
      }
      this.logger?.debug(`Enrichment plugin "${pluginName}" removed.`);
      return true;
    }

    // Check pre-transport plugins
    pluginIndex = this.categorizedPlugins.preTransport.findIndex(p => p.name === pluginName);
    if (pluginIndex !== -1) {
      const [removedPlugin] = this.categorizedPlugins.preTransport.splice(pluginIndex, 1);
      if (removedPlugin.destroy) {
        this.logger?.debug(`Destroying pre-transport plugin "${pluginName}".`);
        removedPlugin.destroy();
      }
      this.logger?.debug(`Pre-transport plugin "${pluginName}" removed.`);
      return true;
    }

    // Check post-transport plugins
    pluginIndex = this.categorizedPlugins.postTransport.findIndex(p => p.name === pluginName);
    if (pluginIndex !== -1) {
      const [removedPlugin] = this.categorizedPlugins.postTransport.splice(pluginIndex, 1);
      if (removedPlugin.destroy) {
        this.logger?.debug(`Destroying post-transport plugin "${pluginName}".`);
        removedPlugin.destroy();
      }
      this.logger?.debug(`Post-transport plugin "${pluginName}" removed.`);
      return true;
    }

    // Check standalone plugins
    pluginIndex = this.standalonePlugins.findIndex(p => p.name === pluginName);
    if (pluginIndex !== -1) {
      const [removedPlugin] = this.standalonePlugins.splice(pluginIndex, 1);
      if (removedPlugin.destroy) {
        this.logger?.debug(`Destroying standalone plugin "${pluginName}".`);
        removedPlugin.destroy();
      }
      this.logger?.debug(`Standalone plugin "${pluginName}" removed.`);
      return true;
    }

    this.logger?.warn(`Plugin "${pluginName}" not found.`);
    return false;
  }

  /**
   * Retrieves a plugin instance by its name.
   * @param pluginName The name of the plugin.
   * @returns The plugin instance if found, otherwise undefined.
   */
  public get(pluginName: string): IPlugin | undefined {
    // Search in enrichment plugins
    let pipelinePlugin = this.categorizedPlugins.enrichment.find(p => p.name === pluginName);
    if (pipelinePlugin) return pipelinePlugin;

    // Search in pre-transport plugins
    pipelinePlugin = this.categorizedPlugins.preTransport.find(p => p.name === pluginName);
    if (pipelinePlugin) return pipelinePlugin;

    // Search in post-transport plugins
    pipelinePlugin = this.categorizedPlugins.postTransport.find(p => p.name === pluginName);
    if (pipelinePlugin) return pipelinePlugin;

    // Search in standalone plugins
    const standalonePlugin = this.standalonePlugins.find(p => p.name === pluginName);
    if (standalonePlugin) return standalonePlugin;

    return undefined;
  }

  /**
   * Returns a list of all registered plugin instances.
   * @returns An object containing all plugin categories.
   */
  public getAll(): {
    enrichment: IPipelinePlugin[];
    preTransport: IPipelinePlugin[];
    postTransport: IPipelinePlugin[];
    standalone: IStandalonePlugin[];
  } {
    return {
      enrichment: this.categorizedPlugins.enrichment,
      preTransport: this.categorizedPlugins.preTransport,
      postTransport: this.categorizedPlugins.postTransport,
      standalone: this.standalonePlugins,
    };
  }

  /**
   * Initializes a specific plugin instance.
   * @param pluginInstance The plugin to initialize.
   */
  public initializePlugin(pluginInstance: IPlugin): void {
    if (!this.sdkConfig) {
      this.logger?.warn(`SDK config not set. Cannot initialize plugin "${pluginInstance.name}".`);
      return;
    }
    if (pluginInstance.initialize) {
      // Get the resolved plugin configuration
      const pluginConfig = getPluginConfig(this.sdkConfig, pluginInstance.name);

      // Validate inheritance configuration
      const pluginSpecificConfig = this.sdkConfig.pluginConfig?.[pluginInstance.name];
      if (pluginSpecificConfig) {
        validateInheritanceConfig(pluginSpecificConfig, `pluginConfig.${pluginInstance.name}`);
      }

      const context: PluginContext = {
        client: this.client,
        config: this.sdkConfig,
        pluginConfig,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        logger: this.logger!,
      };
      this.logger?.debug(`Initializing plugin "${pluginInstance.name}" with config:`, pluginConfig);
      pluginInstance.initialize(context);
      this.logger?.debug(`Plugin "${pluginInstance.name}" initialized successfully.`);
    }
  }

  /**
   * Initializes all registered plugins.
   * Typically called when the SDK itself is initialized.
   */
  public initializeAllPlugins(): void {
    if (!this.sdkConfig) {
      this.logger?.warn('SDK config not set. Cannot initialize plugins');
      return;
    }

    this.logger?.debug('Initializing all registered plugins');

    // Initialize categorized plugins
    const allPipelinePlugins = [
      ...this.categorizedPlugins.enrichment,
      ...this.categorizedPlugins.preTransport,
      ...this.categorizedPlugins.postTransport,
    ];

    allPipelinePlugins.forEach(plugin => {
      this.initializePlugin(plugin);
    });

    // Initialize standalone plugins
    this.standalonePlugins.forEach(plugin => {
      this.initializePlugin(plugin);
    });

    // Start all plugins after initialization
    this.startAllPlugins();
  }

  /**
   * Start all plugins.
   */
  public startAllPlugins(): void {
    this.logger?.debug('Starting all plugins');

    // Start pipeline plugins
    const allPipelinePlugins = [
      ...this.categorizedPlugins.enrichment,
      ...this.categorizedPlugins.preTransport,
      ...this.categorizedPlugins.postTransport,
    ];

    allPipelinePlugins.forEach(plugin => {
      if (plugin.start) {
        this.logger?.debug(`Starting pipeline plugin "${plugin.name}".`);
        plugin.start();
      }
    });

    // Start standalone plugins
    this.standalonePlugins.forEach(plugin => {
      if (plugin.start) {
        this.logger?.debug(`Starting standalone plugin "${plugin.name}".`);
        plugin.start();
      }
    });
  }

  /**
   * Stop all plugins.
   */
  public stopAllPlugins(): void {
    this.logger?.debug('Stopping all plugins');

    // Stop standalone plugins first (in reverse order)
    [...this.standalonePlugins].reverse().forEach(plugin => {
      if (plugin.stop) {
        this.logger?.debug(`Stopping standalone plugin "${plugin.name}".`);
        plugin.stop();
      }
    });

    // Stop categorized plugins (in reverse order)
    const allPipelinePlugins = [
      ...this.categorizedPlugins.postTransport,
      ...this.categorizedPlugins.preTransport,
      ...this.categorizedPlugins.enrichment,
    ];

    allPipelinePlugins.forEach(plugin => {
      if (plugin.stop) {
        this.logger?.debug(`Stopping pipeline plugin "${plugin.name}".`);
        plugin.stop();
      }
    });
  }

  /**
   * Calls the destroy method on all registered plugins, typically in reverse order of initialization.
   * Clears the list of managed plugins.
   */
  public destroyAllPlugins(): void {
    this.logger?.debug('Destroying all plugins');

    // Stop all plugins first
    this.stopAllPlugins();

    // Destroy standalone plugins (in reverse order)
    [...this.standalonePlugins].reverse().forEach(plugin => {
      if (plugin.destroy) {
        this.logger?.debug(`Destroying standalone plugin "${plugin.name}".`);
        plugin.destroy();
      }
    });

    // Destroy categorized plugins (in reverse order)
    const allPipelinePlugins = [
      ...this.categorizedPlugins.postTransport,
      ...this.categorizedPlugins.preTransport,
      ...this.categorizedPlugins.enrichment,
    ];

    allPipelinePlugins.forEach(plugin => {
      if (plugin.destroy) {
        this.logger?.debug(`Destroying pipeline plugin "${plugin.name}".`);
        plugin.destroy();
      }
    });

    // Clear all plugin lists
    this.categorizedPlugins = {
      enrichment: [],
      preTransport: [],
      postTransport: [],
    };
    this.standalonePlugins = [];
  }

  /**
   * Processes an event through the enrichment and pre-transport pipeline phases.
   * This method handles the event processing before it's sent to transport.
   */
  public async processEventThroughPlugins(event: QueuedEvent): Promise<QueuedEvent | null> {
    if (!this.sdkConfig) {
      this.logger?.warn('SDK config not set. Cannot process event through plugins');
      return event;
    }

    if (this.sdkConfig.optOut) {
      this.logger?.debug('Opt-out is enabled. Skipping event processing and dropping event');
      return null;
    }

    let currentEventState: QueuedEvent | null = event;
    this.logger?.debug(
      `Starting event processing pipeline for event "${currentEventState.eventType}". Initial state:`,
      JSON.parse(JSON.stringify(currentEventState))
    );

    // Phase 1: Enrichment plugins
    currentEventState = await this.processEventThroughCategory(
      currentEventState,
      this.categorizedPlugins.enrichment,
      'ENRICHMENT'
    );
    if (!currentEventState) {
      this.logger?.debug(`Event "${event.eventType}" was dropped by enrichment plugins.`);
      return null;
    }

    // Phase 2: Pre-transport plugins
    currentEventState = await this.processEventThroughCategory(
      currentEventState,
      this.categorizedPlugins.preTransport,
      'PRE_TRANSPORT'
    );
    if (!currentEventState) {
      this.logger?.debug(`Event "${event.eventType}" was dropped by pre-transport plugins.`);
      return null;
    }

    this.logger?.debug(
      `Event processing pipeline completed for event "${event.eventType}". Final state:`,
      JSON.parse(JSON.stringify(currentEventState))
    );
    return currentEventState;
  }

  /**
   * Processes an event through post-transport plugins after the transport attempt.
   */
  public async processEventThroughPostTransportPlugins(
    transportResult: TransportResult
  ): Promise<void> {
    if (!this.sdkConfig || this.sdkConfig.optOut) {
      return;
    }

    this.logger?.debug(
      `Processing post-transport plugins for event "${transportResult.event.eventType}". Transport success: ${transportResult.success}`
    );

    for (const plugin of this.categorizedPlugins.postTransport) {
      try {
        // Post-transport plugins receive the transport result, not just the event
        await plugin.processEvent(transportResult.event);
        this.logger?.debug(
          `Post-transport plugin "${plugin.name}" processed event "${transportResult.event.eventType}".`
        );
      } catch (error) {
        this.logger?.error(
          `Error in post-transport plugin "${plugin.name}" for event "${transportResult.event.eventType}":`,
          error
        );
      }
    }
  }

  /**
   * Helper method to process events through a specific plugin category.
   */
  private async processEventThroughCategory(
    event: QueuedEvent | null,
    plugins: IPipelinePlugin[],
    categoryName: string
  ): Promise<QueuedEvent | null> {
    if (!event) return null;

    let currentEventState: QueuedEvent | null = event;
    for (const plugin of plugins) {
      if (!currentEventState) break;

      // Skip inactive plugins
      if (plugin.isActive === false) {
        this.logger?.debug(`Skipping inactive ${categoryName} plugin "${plugin.name}".`);
        continue;
      }

      try {
        this.logger?.debug(
          `Processing event "${currentEventState.eventType}" via ${categoryName} plugin "${plugin.name}".`
        );
        const result = await plugin.processEvent(currentEventState);
        currentEventState = result;

        if (result === null) {
          this.logger?.debug(
            `${categoryName} plugin "${plugin.name}" dropped event "${event.eventType}".`
          );
        } else if (result !== event) {
          this.logger?.debug(
            `${categoryName} plugin "${plugin.name}" modified event "${event.eventType}".`
          );
        }
      } catch (error) {
        this.logger?.error(
          `Error in ${categoryName} plugin "${plugin.name}" for event "${event.eventType}":`,
          error
        );
      }
    }
    return currentEventState;
  }
}
